import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    wrench: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
};

export default function MaintenanceJobs({ user, operatorDetails }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Data for dropdowns
    const [services, setServices] = useState([]);
    const [workshops, setWorkshops] = useState([]);
    const [statuses, setStatuses] = useState([]);

    const [formData, setFormData] = useState({
        jobName: '',
        maintenanceDate: new Date().toISOString().split('T')[0],
        serviceType: '',
        workshopId: '',
        estimatedCost: '',
        status: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
        loadDropdownData();
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const opId = user?.operatorId || user?._id || 'all';
            const res = await axios.get(`${API}/maintenance-jobs/${opId}`);
            setJobs(res.data);
        } catch (err) {
            console.error('Fetch jobs error:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadDropdownData = async () => {
        const opId = user?.operatorId || user?._id || 'all';
        try {
            const [wRes, stRes] = await Promise.all([
                axios.get(`${API}/workshops/${opId}`),
                axios.get(`${API}/master-data`)
            ]);
            setWorkshops(wRes.data);
            setStatuses(stRes.data.filter(d => d.category === 'general-status'));
            setServices(stRes.data.filter(d => d.category === 'service-types'));

            // Set defaults if available
            if (stRes.data.length > 0) {
                const pending = stRes.data.find(s => s.name.toLowerCase().includes('pending') && s.category === 'general-status');
                if (pending) setFormData(prev => ({ ...prev, status: pending.name }));
            }
        } catch (err) {
            console.error('Error loading dropdown data:', err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                operatorId: user?.operatorId || user?._id,
                estimatedCost: parseFloat(formData.estimatedCost) || 0
            };
            if (editingItem) {
                await axios.put(`${API}/maintenance-jobs/${editingItem._id}`, payload);
            } else {
                await axios.post(`${API}/maintenance-jobs`, payload);
            }
            closeModal();
            fetchData();
        } catch (err) {
            console.error('Save job error:', err);
        }
    };

    const handleEdit = (job) => {
        setEditingItem(job);
        setFormData({
            jobName: job.jobName,
            maintenanceDate: job.maintenanceDate ? new Date(job.maintenanceDate).toISOString().split('T')[0] : '',
            serviceType: job.serviceType,
            workshopId: job.workshopId?._id || job.workshopId,
            estimatedCost: (job.estimatedCost || 0).toString(),
            status: job.status,
            notes: job.notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            await axios.delete(`${API}/maintenance-jobs/${id}`);
            fetchData();
        } catch (err) {
            console.error('Delete job error:', err);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        setFormData({
            jobName: '',
            maintenanceDate: new Date().toISOString().split('T')[0],
            serviceType: '',
            workshopId: '',
            estimatedCost: '',
            status: statuses[0]?.name || '',
            notes: ''
        });
    };

    const filteredJobs = jobs.filter(job => {
        const jobDate = job.maintenanceDate ? new Date(job.maintenanceDate).toISOString().split('T')[0] : '';
        if (startDate && jobDate < startDate) return false;
        if (endDate && jobDate > endDate) return false;
        return true;
    });

    return (
        <div className="mj-page">
            <style>{`
                .mj-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .mj-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .mj-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .mj-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .mj-add-btn {
                    background: #1e1b4b; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(30,27,75,0.2);
                }

                .mj-card { 
                    background: white; border-radius: 12px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); overflow: hidden;
                }
                .mj-table { width: 100%; border-collapse: collapse; }
                .mj-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 600; 
                    color: #64748b; text-transform: uppercase; border-bottom: 1px solid #f1f5f9; background: #f8fafc;
                }
                .mj-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
                .mj-table tr:hover { background: #fafbfc; }

                .status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase;
                }

                /* Modal */
                .mj-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .mj-modal { background: white; width: 500px; border-radius: 20px; padding: 30px; position: relative; }
                .mj-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .mj-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                
                .mj-form-group { margin-bottom: 16px; display: flex; flex-direction: column; gap: 6px; }
                .mj-label { font-size: 12px; font-weight: 700; color: #475569; }
                .mj-input, .mj-select {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s;
                }
                .mj-save-btn {
                    width: 100%; background: #6366f1; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; margin-top: 10px;
                }
                .mj-action-btn { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 4px; transition: all 0.2s; }
                .mj-action-btn:hover { background: #f1f5f9; color: #6366f1; }
                .mj-action-btn.del:hover { color: #ef4444; background: #fee2e2; }

                .mj-filters { display: flex; gap: 16px; align-items: flex-end; margin-bottom: 20px; background: white; padding: 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
                .mj-filter-group { display: flex; flex-direction: column; gap: 4px; }
                .mj-filter-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; }
                .mj-filter-input { padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; outline: none; }
                .mj-filter-clear { background: none; border: none; font-size: 12px; color: #6366f1; font-weight: 600; cursor: pointer; margin-bottom: 8px; }
            `}</style>

            <div className="mj-header">
                <div>
                    <h1 className="mj-title">Job Maintenance</h1>
                    <p className="mj-subtitle">Track and manage vehicle maintenance tasks.</p>
                </div>
                <button className="mj-add-btn" onClick={() => setShowModal(true)}>
                    <Icon d={ic.plus} size={18} /> New Job
                </button>
            </div>

            <div className="mj-filters">
                <div className="mj-filter-group">
                    <span className="mj-filter-label">From Date</span>
                    <input type="date" className="mj-filter-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="mj-filter-group">
                    <span className="mj-filter-label">To Date</span>
                    <input type="date" className="mj-filter-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                {(startDate || endDate) && (
                    <button className="mj-filter-clear" onClick={() => { setStartDate(''); setEndDate(''); }}>Clear Filters</button>
                )}
            </div>

            <div className="mj-card">
                <table className="mj-table">
                    <thead>
                        <tr>
                            <th>Job Name</th>
                            <th>Date</th>
                            <th>Job Type</th>
                            <th>Mechanic / Workshop</th>
                            <th>Est. Cost</th>
                            <th>Status</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40 }}>Loading jobs...</td></tr>
                        ) : filteredJobs.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40 }}>No maintenance jobs found.</td></tr>
                        ) : (
                            filteredJobs.map(job => (
                                <tr key={job._id}>
                                    <td style={{ fontWeight: 600 }}>{job.jobName}</td>
                                    <td>{new Date(job.maintenanceDate).toLocaleDateString()}</td>
                                    <td>{job.serviceType}</td>
                                    <td>{job.workshopId?.name || 'N/A'}</td>
                                    <td>{formatCurrency(job.estimatedCost, operatorDetails?.country)}</td>
                                    <td>
                                        <span className="status-badge" style={{
                                            background: '#f1f5f9',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            {job.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className="mj-action-btn" onClick={() => handleEdit(job)} title="Edit"><Icon d={ic.edit} size={16} /></button>
                                            <button className="mj-action-btn del" onClick={() => handleDelete(job._id)} title="Delete"><Icon d={ic.trash} size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="mj-modal-overlay">
                    <div className="mj-modal">
                        <div className="mj-modal-close" onClick={closeModal}>
                            <Icon d={ic.x} size={20} />
                        </div>
                        <h2 className="mj-modal-title">{editingItem ? 'Edit Job Maintenance' : 'Create Job Maintenance'}</h2>
                        <form onSubmit={handleSave}>
                            <div className="mj-form-group">
                                <label className="mj-label">Job Name</label>
                                <input className="mj-input" required placeholder="e.g. Engine Oil Change" value={formData.jobName} onChange={e => setFormData({ ...formData, jobName: e.target.value })} />
                            </div>
                            <div className="mj-form-group">
                                <label className="mj-label">Maintenance Date</label>
                                <input type="date" className="mj-input" required value={formData.maintenanceDate} onChange={e => setFormData({ ...formData, maintenanceDate: e.target.value })} />
                            </div>
                            <div className="mj-form-group">
                                <label className="mj-label">Service Type</label>
                                <select className="mj-select" required value={formData.serviceType} onChange={e => setFormData({ ...formData, serviceType: e.target.value })}>
                                    <option value="">Select...</option>
                                    {services.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="mj-form-group">
                                <label className="mj-label">Mechanic / Workshop</label>
                                <select className="mj-select" required value={formData.workshopId} onChange={e => setFormData({ ...formData, workshopId: e.target.value })}>
                                    <option value="">Select...</option>
                                    {workshops.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div className="mj-form-group">
                                <label className="mj-label">Est. Cost</label>
                                <input type="number" step="0.001" className="mj-input" value={formData.estimatedCost} onChange={e => setFormData({ ...formData, estimatedCost: e.target.value })} placeholder="0.000" />
                            </div>
                            <div className="mj-form-group">
                                <label className="mj-label">Status</label>
                                <select className="mj-select" required value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                    <option value="">Select Status...</option>
                                    {statuses.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                                </select>
                            </div>
                            <button type="submit" className="mj-save-btn">Save</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
